import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';

import {
  createRipple,
  IconButton,
  RippleAnimation,
} from '@/components/common/Buttons/shared';
import { isAnchor } from '@/components/common/Buttons/utils';
import { determineDestination } from '@/components/common/utils';

import type {
  ButtonProps,
  IconButtonProps,
} from '@/components/common/Buttons/shared';

type IconOnlyProps = ButtonProps & IconButtonProps;

export const IconOnlyButton = (props: IconOnlyProps) => {
  const { componentTheme, icon, variant, iconSize } = props;

  if (isAnchor(props)) {
    const { 'aria-label': ariaLabel, onMouseDown, ...rest } = props;
    const destination =
      typeof props?.href === 'string' && determineDestination(props?.href);

    return (
      <IconOnlyBtnLink
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
        rel={destination.isExternalLink ? 'external noopener' : null}
        variant={variant}
        componentTheme={componentTheme}
        {...rest}
      >
        <IconButton
          icon={icon}
          componentTheme={componentTheme}
          variant={variant}
          iconSize={iconSize}
        />
      </IconOnlyBtnLink>
    );
  } else {
    const { onMouseDown, ...rest } = props;

    return (
      <IconOnlyBtn
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        variant={variant}
        componentTheme={componentTheme}
        {...rest}
      >
        <IconButton
          icon={icon}
          componentTheme={componentTheme}
          variant={variant}
          iconSize={iconSize}
        />
      </IconOnlyBtn>
    );
  }
};
const CoreIconOnlyStyles = css`
  border-radius: var(--radius-full);
  &.disabled,
  &:disabled,
  &.slick-disabled {
    opacity: var(--opacity-disabled);
    pointer-events: none;
  }
`;

const IconOnlyBtnLink = styled(Link, { shouldForwardProp: isPropValid })<{
  variant: 'primary' | 'secondary' | 'tertiary';
  componentTheme: 'light' | 'dark';
}>`
  ${CoreIconOnlyStyles}
  display: inline-flex;
  width: fit-content;

  ${({ variant }) => css`
    ${RippleAnimation(variant)}
  `}
  ${({ componentTheme }) => css`
    ${IconOnlyStyles(componentTheme)};
  `};
`;

const IconOnlyBtn = styled.button<{
  variant: 'primary' | 'secondary' | 'tertiary';
  componentTheme: 'light' | 'dark';
}>`
  ${CoreIconOnlyStyles}
  background: unset;
  border: unset;
  cursor: pointer;
  padding: 0;

  ${({ variant }) => css`
    ${RippleAnimation(variant)}
  `}

  ${({ componentTheme }) => css`
    ${IconOnlyStyles(componentTheme)};
  `}
`;

const IconOnlyStyles = (componentTheme) => {
  if (componentTheme === 'light')
    return css`
      &:focus-visible {
        outline: 2px solid var(--border-action-dark);
        outline-offset: 2px;
      }
    `;
  if (componentTheme === 'dark')
    return css`
      &:focus-visible {
        outline: 2px solid var(--decorative-dark-eclipse);
        outline-offset: 2px;
      }
    `;
};
